import { serverDateSort } from 'components/Tables/dateSorting';
import React, { useContext, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { FilterDropdownProps } from 'antd/es/table/interface';

import useTable from 'components/CCCTable/useTable';
import CCCTable from 'components/CCCTable/CCCTable';

import routes from 'constants/routes';
import { DATE, TIME } from 'constants/dateformats';

import {
  QUERY_CONTAINER_STATUSES,
  QUERY_LIST_CONTAINERS,
  MUTATION_SET_QR_BATCH,
  PURE_QUERY_LIST_CONTAINERS,
  MUTATION_SET_CORPORATE_CLIENT_TO_CONTAINERS,
  QUERY_GET_QR_BATCHES,
  QUERY_GET_LOCATION_FILTERS,
  QUERY_TOTAL_CONTAINERS,
} from './queries';
import AssignLocationAndStatusModal from './components/AssignLocationAndStatusModal';
import ChangeStatusModal from './components/ChangeStatusModal';
import AssignContainerTypeConfirmModal from './components/AssignContainerTypeConfirmModal';
import {
  QUERY_LIST_CONTAINER_TYPES,
  QUERY_LIST_CORPORATE_CLIENTS,
} from '../../../queries/DictionariesQueries';
import { UserContext } from '../../../contexts/UserContext';
import { useMutation } from '@apollo/client';
import printOptions from '../../../constants/PrintOptions';
import PermissionsAlert from '../../../components/PermissionsAlert/PermissionsAlert';
import Loader from '../../../components/Loader/Loader';
import auth from '../../../services/Auth/Firebase';
import { getAdminPanelFetchHeaders } from '../../../services/adminPanelRequestHeaders';
import { resolvePrintApiBaseUrl } from '../../../services/resolveGraphqlHttpUri';
import { openPrintResponse } from '../../../utils/openPrintResponse';

import CustomFilterDropdown from 'components/CCCTable/FilterDropdown';
import { notification } from 'antd';

const Index = () => {
  const { dbUser } = useContext(UserContext);
  const {
    setTableRef,
    mounted,
    refetch,
    setSelectedRowKeys,
    getColumnSearchProps,
    getColumnNumberSearch,
    getColumnRadioFilterProps,
  } = useTable();
  const [assignLocationAndStatusRecords, setAssignLocationAndStatusRecords] =
    useState<any[]>([]);
  const [changeStatus, setChangeStatus] = useState<any>();
  const [isHoveringDate, setIsHoveringDate] = useState(false);
  const [
    assignContainerTypeConfirmModalProps,
    setAssignContainerTypeConfirmModalProps,
  ] = useState<{
    containerIds: number[];
    nextContainerTypeId: number;
  } | null>(null);

  const {
    data: cupStatuses,
    error: cupStatusesError,
    loading: cupStatusesLoading,
  } = useQuery(QUERY_CONTAINER_STATUSES);
  const {
    data: containerTypes,
    error: containerTypesError,
    loading: containerTypesLoading,
  } = useQuery(QUERY_LIST_CONTAINER_TYPES);
  const {
    data: corpClients,
    error: corpClientsError,
    loading: corpClientsLoading,
  } = useQuery(QUERY_LIST_CORPORATE_CLIENTS, {
    variables: {
      order: [{ field: 'name', order: 'ASC' }],
      where: dbUser?.isCorporateAdmin
        ? {
            corporate_client_id: {
              in: dbUser?.userCorporateClients?.map(
                (uc) => uc.corporate_client_id,
              ),
            },
          }
        : {},
    },
  });
  const [setQrBatch] = useMutation(MUTATION_SET_QR_BATCH, {
    refetchQueries: [PURE_QUERY_LIST_CONTAINERS],
  });

  const [setCorporateClientToContainers] = useMutation(
    MUTATION_SET_CORPORATE_CLIENT_TO_CONTAINERS,
    {
      refetchQueries: [PURE_QUERY_LIST_CONTAINERS],
    },
  );

  const columns = useMemo(
    () => [
      {
        title: 'Container Name',
        dataIndex: 'unique_name',
        render: (unique_name: string, record: any) => (
          <Link to={routes.CONTAINERS + '/' + record.container_id}>
            {unique_name}
          </Link>
        ),
        sorter: true,
        filterOperation: 'contains',

        ...(mounted && getColumnSearchProps('unique_name')),
        width: 160,
      },
      {
        title: 'Type',
        dataIndex: ['type', 'name'],
        key: 'type_id',
        sorter: true,

        filters:
          containerTypes && !containerTypesError
            ? [
                ...containerTypes.listContainerType.map((s: any) => ({
                  value: s.type_id,
                  text: s.name,
                })),
                {
                  value: null,
                  text: 'NULL',
                },
              ]
            : [],
        width: 120,
      },
      {
        title: 'SKU',
        dataIndex: ['type', 'sku'],
        key: 'type.sku',
        sorter: true,

        filters:
          containerTypes && !containerTypesError
            ? [
                ...containerTypes.listContainerType
                  .filter((t: any) => t.sku)
                  .map((t: any) => ({
                    value: t.sku,
                    text: t.sku,
                  })),
                {
                  value: null,
                  text: 'NULL',
                },
              ]
            : [],
        width: 100,
      },
      {
        title: 'Status',
        dataIndex: ['status', 'name'],
        key: 'current_status_id',
        sorter: true,

        filters:
          cupStatuses && !cupStatusesError
            ? cupStatuses.listContainerStatus.map((s: any) => ({
                value: s.status_id,
                text: s.name,
              }))
            : [],
        width: 120,
      },
      {
        title: 'Status Updated On',
        dataIndex: 'status_updated_on',
        key: 'status_updated_on',
        ...serverDateSort(),
        render: (status_updated_on: string) =>
          dayjs(status_updated_on).format(DATE),
        width: 150,
      },
      {
        title: 'Corporate Client',
        dataIndex: ['corporateClient', 'name'],
        key: 'corporate_client_id',
        sorter: true,
        filters:
          corpClients && !corpClientsError
            ? [
                ...corpClients.listCorporateClient.map((l: any) => ({
                  value: l.corporate_client_id,
                  text: l.name,
                })),
                ...(dbUser?.isCorporateAdmin
                  ? []
                  : [
                      {
                        value: null,
                        text: 'NOT ASSIGNED',
                      },
                    ]),
              ]
            : [],
        width: 180,
      },
      {
        title: 'Last Location',
        dataIndex: ['last_location', 'name'],
        key: 'last_location_id',
        sorter: true,

        render: (loc_name: string, { last_location }: any) =>
          last_location ? (
            dbUser?.userPermissions.ManageLocations ? (
              <Link
                to={
                  routes.LOCATIONS + '/' + last_location.location_id + '/edit'
                }
              >
                {loc_name}
              </Link>
            ) : (
              loc_name
            )
          ) : null,
        filterDropdown: (dropdownOptions: FilterDropdownProps) => (
          <CustomFilterDropdown
            {...dropdownOptions}
            queryVariables={{
              corporate_client_id:
                dbUser?.userCorporateClients?.find(
                  (uc) => uc.corporate_client_id,
                )?.corporate_client_id || 0,
            }}
            query={QUERY_GET_LOCATION_FILTERS}
            normalizeItems={(data) =>
              data.getLocationFilters.map((l: any) => ({
                value: l.location_id,
                text: l.name,
              }))
            }
          />
        ),
        width: 160,
      },
      {
        title: 'Number Of Assignments',
        dataIndex: 'assignments_amount',
        sorter: true,
        width: 180,
        filterOperation: 'between',
        ...(mounted && getColumnNumberSearch('assignments amount')),
      },
      {
        title: 'QRCode printed on',
        dataIndex: 'qr_code_printed_on',
        ...serverDateSort(),
        render: (qr_code_printed_on: string) =>
          qr_code_printed_on
            ? dayjs(qr_code_printed_on).format(DATE)
            : 'not printed',

        filterOperation: 'isNull',
        filters: [
          {
            value: true,
            text: 'Not set',
          },
          {
            value: false,
            text: 'Exist',
          },
        ],
        ...(mounted && getColumnRadioFilterProps('qr_code_printed_on')),
        width: 180,
      },
      {
        title: 'QR Batch',
        dataIndex: 'qr_batch',
        sorter: true,
        render: (qr_batch: string) => (qr_batch ? qr_batch : 'not assigned'),
        filterDropdown: (dropdownOptions: FilterDropdownProps) => (
          <CustomFilterDropdown
            {...dropdownOptions}
            queryVariables={{
              corporate_client_id:
                dbUser?.userCorporateClients?.find(
                  (uc) => uc.corporate_client_id,
                )?.corporate_client_id || 0,
            }}
            query={QUERY_GET_QR_BATCHES}
            normalizeItems={(data) =>
              data.qrBatchList.map((batch: number) => ({
                value: batch,
                text: batch ? batch.toString() : 'NOT ASSIGNED',
              }))
            }
          />
        ),

        width: 180,
      },
      {
        title: 'Created on',
        dataIndex: 'created_at',
        ...serverDateSort(true),
        render: (date: string) => (
          <div>
            <div
              onMouseOver={() => setIsHoveringDate(true)}
              onMouseOut={() => setIsHoveringDate(false)}
            >
              {dayjs(date).format(DATE)}
            </div>
            {isHoveringDate && <div>{dayjs(date).format(TIME)}</div>}
          </div>
        ),
        width: 180,
      },
    ],
    //eslint-disable-next-line
    [cupStatuses, cupStatusesError, mounted],
  );

  const actions: any[] = [
    {
      label: 'Change status',
      onlyOne: true,
      run: ([cup]: any[]) => {
        setChangeStatus(cup);
      },
    },
    {
      label: 'Print QR Codes',
      type: 'primary',
      isDropdown: true,
      dropdownItems: printOptions,
      checkDisabled: (records: any[]) =>
        records?.length > 1 &&
        !records.every(
          (record, index, array) =>
            index === array.length - 1 ||
            record?.type?.type_id === array[index + 1]?.type?.type_id,
        ),
      runItem: async (records: any[], key: string) => {
        const printWindow = window.open('', '_blank');

        try {
          const printApi = resolvePrintApiBaseUrl();
          const token = await auth.currentUser?.getIdToken();
          const container_ids = records.map((c) => c.container_id);
          const qrBatchResult = await setQrBatch({
            variables: { container_ids },
          });
          const batches = qrBatchResult?.data?.setQrBatch || [];

          if (!batches.length) {
            printWindow?.close();
            notification.warning({
              message: 'No QR batches available for print',
            });
            return;
          }

          const printUrl =
            printApi +
            `/print?type=container&format=${key}&batch_ids=` +
            batches.join(',');
          const response = await fetch(printUrl, {
            headers: getAdminPanelFetchHeaders(token),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
              errorText || `Failed to load print file: ${response.status}`,
            );
          }

          await openPrintResponse(response, key, printWindow);
        } catch (error) {
          printWindow?.close();
          notification.error({
            message: 'Failed to print QR codes',
            description: error instanceof Error ? error.message : undefined,
          });
        }
      },
    },
    {
      label: 'Set Corporate Client',
      type: 'primary',
      isDropdown: true,
      dropdownItems:
        corpClients && !corpClientsError
          ? corpClients.listCorporateClient.map((l: any) => ({
              label: l.name,
              key: l.corporate_client_id,
            }))
          : [],
      runItem: async (records: any[], key: string) => {
        await setCorporateClientToContainers({
          variables: {
            container_ids: records.map((c) => c.container_id),
            corporate_client_id: key,
          },
        });
      },
    },
    {
      label: 'Assign Container Type',
      type: 'primary',
      isDropdown: true,
      dropdownItems:
        containerTypes && !containerTypesError
          ? containerTypes.listContainerType.map(
              (it: { name: string; type_id: number }) => ({
                key: it.type_id,
                label: it.name,
              }),
            )
          : [],
      runItem: async (
        records: { container_id: number }[],
        next_type_id: string,
      ) => {
        if (!dbUser?.userPermissions.ManageCups) {
          notification.error({
            message: 'You do not have permission to assign container type',
          });

          return;
        }

        setAssignContainerTypeConfirmModalProps({
          containerIds: records.map((c) => c.container_id),
          nextContainerTypeId: Number(next_type_id),
        });
      },
    },
    {
      label: 'Create Containers',
      type: 'primary',
      to: routes.CONTAINERS + '/create',
    },
  ];

  if (cupStatusesLoading || corpClientsLoading || containerTypesLoading) {
    return <Loader />;
  }

  if (
    !dbUser?.userPermissions.ManageCups &&
    !dbUser?.userPermissions.ViewCups
  ) {
    return <PermissionsAlert />;
  }

  return (
    <>
      <AssignLocationAndStatusModal
        visible={!!assignLocationAndStatusRecords?.length}
        records={assignLocationAndStatusRecords}
        onClose={async (updated: boolean) => {
          setAssignLocationAndStatusRecords([]);

          if (updated) {
            setSelectedRowKeys([]);
            refetch();
          }
        }}
      />
      <ChangeStatusModal
        visible={!!changeStatus}
        record={changeStatus}
        onClose={async (updated: boolean) => {
          setChangeStatus(false);

          if (updated) {
            setSelectedRowKeys([]);
            refetch();
          }
        }}
      />
      <AssignContainerTypeConfirmModal
        open={!!assignContainerTypeConfirmModalProps}
        onClose={() => setAssignContainerTypeConfirmModalProps(null)}
        containerIds={assignContainerTypeConfirmModalProps?.containerIds}
        nextContainerTypeId={
          assignContainerTypeConfirmModalProps?.nextContainerTypeId
        }
      />
      <CCCTable
        ref={setTableRef}
        query={QUERY_LIST_CONTAINERS}
        totalQuery={QUERY_TOTAL_CONTAINERS}
        queryKey="listContainer"
        totalQueryKey="totalContainer"
        rowKey="container_id"
        columns={columns}
        actions={dbUser?.userPermissions.ManageCups ? actions : []}
      />
    </>
  );
};

export default Index;
